import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
    Loader2, Search, Building2, Landmark, User, ChevronLeft, ChevronRight,
    Database, X, DownloadCloud, ExternalLink,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import ImportDiffDialog from './ImportDiffDialog';
import BatchImportDialog from './BatchImportDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 25;
const MAX_BATCH = 50;

const ENTITY_TABS = [
    { key: 'company',  label: 'Companies', Icon: Building2 },
    { key: 'investor', label: 'Investors', Icon: Landmark },
    { key: 'person',   label: 'People',    Icon: User },
];

const CRM_FILTERS = [
    { key: '',           label: 'All' },
    { key: 'linked',     label: 'In CRM' },
    { key: 'not_linked', label: 'Not imported' },
];

function CrmStatusBadge({ item }) {
    if (item.crm_status === 'linked') {
        const href = item.entity === 'person' ? `/contacts/${item.crm_id}` : `/companies/${item.crm_id}`;
        return (
            <a href={href} target="_blank" rel="noreferrer"
                title={item.crm_name ? `Open ${item.crm_name}` : 'Open CRM record'}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100 hover:bg-emerald-100 whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                In CRM <ExternalLink className="w-2.5 h-2.5" />
            </a>
        );
    }
    if (item.crm_status === 'match') {
        return (
            <span title={item.crm_name ? `Matches ${item.crm_name} — importing updates it, no duplicate` : undefined}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700 ring-1 ring-blue-100 whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                Match in CRM
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-stone-100 text-stone-500 ring-1 ring-stone-200 whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full bg-stone-400" />
            Not in CRM
        </span>
    );
}

export default function PitchBookDataBrowser({ refreshKey = 0, onImported }) {
    const { getAuthHeader } = useAuth();
    const [entity, setEntity] = useState('company');
    const [search, setSearch] = useState('');
    const [crmFilter, setCrmFilter] = useState('');
    const [skip, setSkip] = useState(0);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [importTarget, setImportTarget] = useState(null);
    const [batchOpen, setBatchOpen] = useState(false);
    const debounceRef = useRef(null);

    // Selection: { [pitchbook_id]: { name, entity } } — survives pagination / search.
    // Only populated for company/investor tabs (person bulk import is deferred).
    const [selection, setSelection] = useState({});
    const selectionCount = Object.keys(selection).length;

    const load = useCallback(async (params) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/pitchbook/browse`, {
                params: { limit: PAGE_SIZE, ...params },
                headers: getAuthHeader(),
            });
            setData(res.data);
        } catch {
            setData({ indexed: false, items: [], totals: {}, has_more: false });
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader]);

    // Reload on tab/filter/page changes + external refresh (e.g. after an index run).
    useEffect(() => {
        load({ entity, q: search, skip, crm: crmFilter });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entity, skip, crmFilter, refreshKey, load]);

    // Debounced search.
    const onSearchChange = (value) => {
        setSearch(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSkip(0);
            load({ entity, q: value, skip: 0, crm: crmFilter });
        }, 350);
    };
    useEffect(() => () => debounceRef.current && clearTimeout(debounceRef.current), []);

    const switchEntity = (key) => {
        setEntity(key);
        setSkip(0);
        setCrmFilter('');
        // Keep cross-tab selection intact — admin may want company + investor mix.
    };

    const totals = data?.totals || {};
    const items = data?.items || [];
    const page = Math.floor(skip / PAGE_SIZE) + 1;

    // IDs visible on the current page (company/investor tabs only).
    const selectableItems = useMemo(
        () => items.filter((i) => i.entity !== 'person'),
        [items]);
    const pageIds = useMemo(() => selectableItems.map((i) => i.pitchbook_id), [selectableItems]);
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selection[id]);
    const somePageSelected = pageIds.some((id) => selection[id]);

    const toggleItem = (item) => {
        setSelection((s) => {
            const next = { ...s };
            if (next[item.pitchbook_id]) {
                delete next[item.pitchbook_id];
            } else {
                if (Object.keys(next).length >= MAX_BATCH) return s; // cap
                next[item.pitchbook_id] = { name: item.name, entity: item.entity };
            }
            return next;
        });
    };

    // Header checkbox: if ANY page rows are selected, clicking unselects them;
    // only a fully unselected page selects everything (up to the cap).
    const toggleAllPage = () => {
        if (somePageSelected) {
            setSelection((s) => {
                const next = { ...s };
                pageIds.forEach((id) => delete next[id]);
                return next;
            });
        } else {
            setSelection((s) => {
                const next = { ...s };
                let added = 0;
                for (const item of selectableItems) {
                    if (!next[item.pitchbook_id]) {
                        if (Object.keys(next).length + added >= MAX_BATCH) break;
                        next[item.pitchbook_id] = { name: item.name, entity: item.entity };
                        added++;
                    }
                }
                return next;
            });
        }
    };

    const clearSelection = () => setSelection({});

    const openImport = (item) => {
        if (item.entity === 'person') {
            if (!item.company_pb_id) return;
            setImportTarget({
                pitchbookId: item.company_pb_id,
                recordName: item.company_name || item.name,
                personFocusId: item.pitchbook_id,
            });
        } else {
            setImportTarget({ pitchbookId: item.pitchbook_id, recordName: item.name });
        }
    };

    const isPersonTab = entity === 'person';
    // Checkbox column only on company/investor tabs.
    const columns = useMemo(() => {
        if (isPersonTab) return ['Name', 'Title', 'Company', 'Email', 'CRM status', ''];
        return ['', 'Name', 'Location', 'Type', 'Website', 'CRM status', ''];
    }, [isPersonTab]);

    return (
        <div className="bg-white">
            {/* Tabs + search */}
            <div className="flex flex-wrap items-center gap-3 px-4 pt-4 pb-3 border-b border-stone-100">
                <div className="inline-flex p-1 rounded-xl bg-stone-100">
                    {ENTITY_TABS.map(({ key, label, Icon }) => {
                        const active = entity === key;
                        return (
                            <button key={key} onClick={() => switchEntity(key)}
                                className={`flex items-center gap-2 px-3.5 py-1.5 text-sm font-medium rounded-lg transition-all
                                    ${active ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}>
                                <Icon className="w-4 h-4" />{label}
                                <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-semibold
                                    ${active ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-500'}`}>
                                    {(totals[key] ?? 0).toLocaleString()}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <div className="relative flex-1 min-w-[200px] max-w-xs ml-auto">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
                    <Input value={search} onChange={(e) => onSearchChange(e.target.value)}
                        placeholder={entity === 'person' ? 'Search people, companies, emails…' : 'Search names…'}
                        className="pl-9 h-8 text-xs" />
                    {search && (
                        <button onClick={() => onSearchChange('')}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>

                <div className="inline-flex p-0.5 bg-stone-100 rounded-lg">
                    {CRM_FILTERS.map((f) => (
                        <button key={f.key}
                            onClick={() => { setCrmFilter(f.key); setSkip(0); }}
                            className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-all
                                ${crmFilter === f.key ? 'bg-white shadow-sm text-stone-800' : 'text-stone-400 hover:text-stone-600'}`}>
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Table */}
            {loading && !data ? (
                <div className="py-12 text-center text-stone-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
            ) : data && data.indexed === false ? (
                <div className="py-12 text-center text-stone-500">
                    <Database className="w-8 h-8 mx-auto mb-2 text-stone-300" />
                    <p className="text-sm font-medium">No PitchBook data indexed yet</p>
                    <p className="text-xs text-stone-400 mt-1">New exports are indexed automatically — or index a file by hand from the bucket files dialog.</p>
                </div>
            ) : (
                <>
                    <div className={`overflow-x-auto transition-opacity ${loading ? 'opacity-50' : ''}`}>
                        <table className="w-full text-xs">
                            <thead className="bg-stone-50 text-stone-500">
                                <tr>
                                    {columns.map((c, i) => {
                                        if (i === 0 && !isPersonTab) {
                                            return (
                                                <th key="cb" className="px-3 py-2 w-8">
                                                    <input
                                                        type="checkbox"
                                                        checked={allPageSelected}
                                                        ref={(el) => {
                                                            if (el) el.indeterminate = somePageSelected && !allPageSelected;
                                                        }}
                                                        onChange={toggleAllPage}
                                                        className="rounded border-stone-300 text-stone-900 focus:ring-stone-700 cursor-pointer align-middle"
                                                        title={somePageSelected ? 'Unselect page' : 'Select page'}
                                                    />
                                                </th>
                                            );
                                        }
                                        return (
                                            <th key={i} className={`px-4 py-2 font-medium whitespace-nowrap ${i === columns.length - 1 ? 'text-right' : 'text-left'}`}>{c}</th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                                {items.length === 0 && !loading && (
                                    <tr>
                                        <td colSpan={columns.length} className="px-4 py-8 text-center text-stone-400">
                                            No records match.
                                        </td>
                                    </tr>
                                )}
                                {items.map((item) => {
                                    const isSelected = !!selection[item.pitchbook_id];
                                    const atCap = selectionCount >= MAX_BATCH && !isSelected;
                                    return (
                                        <tr key={item.pitchbook_id}
                                            className={`hover:bg-stone-50 ${isSelected ? 'bg-stone-100/60' : ''}`}>
                                            {!isPersonTab && (
                                                <td className="px-3 py-2.5">
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        disabled={atCap}
                                                        onChange={() => toggleItem(item)}
                                                        className={`rounded border-stone-300 text-stone-900 focus:ring-stone-700 align-middle ${atCap ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
                                                        title={atCap ? `Maximum ${MAX_BATCH} records` : undefined}
                                                    />
                                                </td>
                                            )}
                                            <td className="px-4 py-2.5">
                                                <p className="font-medium text-stone-800 truncate max-w-[260px]" title={item.name}>{item.name || '—'}</p>
                                                <p className="text-[10px] text-stone-400 font-mono">{item.pitchbook_id}</p>
                                            </td>
                                            {item.entity === 'person' ? (
                                                <>
                                                    <td className="px-4 py-2.5 text-stone-600 truncate max-w-[180px]" title={item.title}>{item.title || '—'}</td>
                                                    <td className="px-4 py-2.5 text-stone-600 truncate max-w-[200px]" title={item.company_name}>{item.company_name || '—'}</td>
                                                    <td className="px-4 py-2.5 text-stone-500 truncate max-w-[200px]" title={item.email}>{item.email || '—'}</td>
                                                </>
                                            ) : (
                                                <>
                                                    <td className="px-4 py-2.5 text-stone-600 whitespace-nowrap">
                                                        {[item.city, item.country].filter(Boolean).join(', ') || '—'}
                                                    </td>
                                                    <td className="px-4 py-2.5 text-stone-600 truncate max-w-[160px]" title={item.type}>{item.type || '—'}</td>
                                                    <td className="px-4 py-2.5 text-stone-500 truncate max-w-[180px]" title={item.website}>{item.website || '—'}</td>
                                                </>
                                            )}
                                            <td className="px-4 py-2.5"><CrmStatusBadge item={item} /></td>
                                            <td className="px-4 py-2.5 text-right">
                                                <Button size="sm" variant="outline" className="h-7 text-[11px]"
                                                    disabled={item.entity === 'person' && !item.company_pb_id}
                                                    title={item.entity === 'person' && !item.company_pb_id
                                                        ? 'No primary company in the indexed data'
                                                        : undefined}
                                                    onClick={() => openImport(item)}>
                                                    <DownloadCloud className="w-3 h-3 mr-1" />
                                                    {item.crm_status === 'none' ? 'Import' : 'Update'}
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between px-4 py-3 border-t border-stone-100">
                        <p className="text-xs text-stone-400">
                            Page {page}{search ? ' · filtered' : ''} · {items.length} shown
                        </p>
                        <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" disabled={skip === 0 || loading}
                                onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}>
                                <ChevronLeft className="w-3.5 h-3.5" /> Prev
                            </Button>
                            <Button size="sm" variant="outline" disabled={!data?.has_more || loading}
                                onClick={() => setSkip(skip + PAGE_SIZE)}>
                                Next <ChevronRight className="w-3.5 h-3.5" />
                            </Button>
                        </div>
                    </div>
                </>
            )}

            {/* Floating bulk-action bar — portaled to <body> so position:fixed
                tracks the viewport (a transformed ancestor would otherwise
                trap it inside this component). */}
            {selectionCount > 0 && !batchOpen && createPortal((
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-8 pl-6 pr-3 py-2.5 bg-white border border-stone-200 rounded-full shadow-lg shadow-stone-200/60">
                    <div className="flex items-center gap-2 text-xs text-stone-800 whitespace-nowrap">
                        <span className="font-medium">{selectionCount} selected</span>
                        {selectionCount >= MAX_BATCH && (
                            <span className="text-stone-400">(max {MAX_BATCH})</span>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button onClick={clearSelection}
                            className="text-xs text-stone-500 hover:text-stone-800 px-3 py-1.5 rounded-full hover:bg-stone-100">
                            Clear
                        </button>
                        <Button size="sm" className="h-9 px-5 text-xs rounded-full"
                            onClick={() => setBatchOpen(true)}>
                            Import {selectionCount} selected
                        </Button>
                    </div>
                </div>
            ), document.body)}

            {importTarget && (
                <ImportDiffDialog
                    pitchbookId={importTarget.pitchbookId}
                    recordName={importTarget.recordName}
                    personFocusId={importTarget.personFocusId || null}
                    onClose={() => setImportTarget(null)}
                    onCommitted={() => {
                        load({ entity, q: search, skip, crm: crmFilter });
                        onImported?.();
                    }}
                />
            )}

            {batchOpen && (
                <BatchImportDialog
                    pitchbookIds={Object.keys(selection)}
                    selectionMeta={selection}
                    onClose={() => setBatchOpen(false)}
                    onCommitted={() => {
                        clearSelection();
                        load({ entity, q: search, skip, crm: crmFilter });
                        onImported?.();
                    }}
                />
            )}
        </div>
    );
}
